import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel,
  Checkbox,
} from '@mui/material'
import { useState } from 'react'

export interface Column<T> {
  id: keyof T | 'actions'
  label: string
  minWidth?: number
  align?: 'right' | 'left' | 'center'
  sortable?: boolean
  render?: (value: any, row: T) => React.ReactNode
}

export interface TableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  onRowClick?: (row: T) => void
  pagination?: {
    page: number
    rowsPerPage: number
    total: number
    onPageChange: (page: number) => void
    onRowsPerPageChange: (rowsPerPage: number) => void
  }
  sortable?: boolean
  onSort?: (column: keyof T, direction: 'asc' | 'desc') => void
  selectable?: boolean
  selectedRows?: T[]
  onSelectionChange?: (selectedRows: T[]) => void
  emptyMessage?: string
}

function Table<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  onRowClick,
  pagination,
  sortable = false,
  onSort,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  emptyMessage = 'No data available',
}: TableProps<T>) {
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: keyof T) => {
    if (!sortable || !onSort || column === 'actions') return

    const isAsc = sortColumn === column && sortDirection === 'asc'
    const newDirection = isAsc ? 'desc' : 'asc'
    
    setSortColumn(column)
    setSortDirection(newDirection)
    onSort(column, newDirection)
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectable || !onSelectionChange) return

    if (event.target.checked) {
      onSelectionChange(data)
    } else {
      onSelectionChange([])
    }
  }

  const handleSelectRow = (row: T) => {
    if (!selectable || !onSelectionChange) return

    const rowId = (row as any).id || (row as any)._id
    const isSelected = selectedRows.some(selected => {
      const selectedId = (selected as any).id || (selected as any)._id
      return selectedId === rowId
    })
    if (isSelected) {
      onSelectionChange(selectedRows.filter(selected => {
        const selectedId = (selected as any).id || (selected as any)._id
        return selectedId !== rowId
      }))
    } else {
      onSelectionChange([...selectedRows, row])
    }
  }

  const isSelected = (row: T) => {
    const rowId = (row as any).id || (row as any)._id
    return selectedRows.some(selected => {
      const selectedId = (selected as any).id || (selected as any)._id
      return selectedId === rowId
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <MuiTable stickyHeader>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {sortable && column.sortable && column.id !== 'actions' ? (
                    <TableSortLabel
                      active={sortColumn === column.id}
                      direction={sortColumn === column.id ? sortDirection : 'asc'}
                      onClick={() => handleSort(column.id as keyof T)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rowId = (row as any).id || (row as any)._id
                return (
                <TableRow
                  key={rowId}
                  hover
                  selected={isSelected(row)}
                  onClick={(event) => {
                    if (event.defaultPrevented || (event.target as Element).closest('button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [contenteditable="true"]')) return
                    if (onRowClick) onRowClick(row)
                    else if (selectable) handleSelectRow(row)
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (onRowClick && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault()
                      onRowClick(row)
                    }
                  }}
                  sx={{
                    cursor: selectable || onRowClick ? 'pointer' : 'default',
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox checked={isSelected(row)} onChange={() => handleSelectRow(row)} />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={String(column.id)}
                      align={column.align}
                      onClick={column.id === 'actions' ? (event) => event.stopPropagation() : undefined}
                    >
                      {column.render
                        ? column.render(column.id === 'actions' ? null : row[column.id as keyof T], row)
                        : String(column.id === 'actions' ? '' : (row[column.id as keyof T] || ''))}
                    </TableCell>
                  ))}
                </TableRow>
                )
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
          onRowsPerPageChange={(event) =>
            pagination.onRowsPerPageChange(parseInt(event.target.value, 10))
          }
        />
      )}
    </Paper>
  )
}

export default Table
